import React from 'react';
import { Route } from 'react-router-dom';
// import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { TextSize, LightBox, ExpandableNavigation } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';

// Setup for Scaffold Field Extensions
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';

// Theme
// import { ThemeProvider } from '@material-ui/core/styles';
// import CssBaseline from '@material-ui/core/CssBaseline';
import LightIcon from '@material-ui/icons/WbSunny';
import { UnifiedThemeProvider} from '@backstage/theme';
import { qmuTheme } from './theme/qmuTheme';

// Icons
import { ExampleIcon } from './assets/CustomIcons';
import AlarmIcon from '@material-ui/icons/Alarm';

// HomePage
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';

// S3 Viewer
// import { S3ViewerPage } from '@spreadshirt/backstage-plugin-s3-viewer';

// keycloak auth apiRef
import { keycloakAuthApiRef } from './apis'

// Grafana
import { grafanaPlugin } from '@k-phoen/backstage-plugin-grafana';

// Harbor Plugins
import { HarborImageListExtension } from './scaffolder/HarborImageList';
import { K8SExecPage } from '@internal/backstage-plugin-k8s-exec';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { NotebookStylesAddon } from './components/techdocs/NotebookStylesAddon';
import CustomTaskPage from './components/scaffolder/CustomTaskPage';
import TemplateListPageWithTasks from './components/scaffolder/TemplateListPageWithTasks';
import { TemplateListPageProps } from '@backstage/plugin-scaffolder/alpha';
import { DashboardsPage } from './components/dashboards/DashboardsPage';

// Prevent double-create: disables Scaffolder "Create" button after first click
const PreventDoubleCreate: React.FC = () => {
  React.useEffect(() => {
    // Poll until the button exists on the page
    const interval = setInterval(() => {
      // The create button label is localized; match by role and text fallback
      const buttons = Array.from(document.querySelectorAll('button')) as HTMLButtonElement[];
      const createBtn = buttons.find(b => /create/i.test(b.textContent || ''));
      if (!createBtn) return;

      // If already wired, stop
      if ((createBtn as any)._doubleClickGuardInstalled) {
        clearInterval(interval);
        return;
      }

      (createBtn as any)._doubleClickGuardInstalled = true;
      let clicked = false;
      const onClick = () => {
        if (clicked) return;
        clicked = true;
        createBtn.disabled = true;
        createBtn.style.opacity = '0.6';
        createBtn.style.pointerEvents = 'none';
      };
      createBtn.addEventListener('click', onClick, { once: true });
      clearInterval(interval);
    }, 200);

    return () => clearInterval(interval);
  }, []);

  return null;
};

const app = createApp({
  apis,
  plugins: [grafanaPlugin],
  themes: [{
    id: 'qmu-theme',
    title: 'QMUL Theme',
    variant: 'light',
    icon: <LightIcon />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={qmuTheme} children={children} />
    ),
  }],
  icons: {
    github: ExampleIcon,
    alert: AlarmIcon,
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    // bind(apiDocsPlugin.externalRoutes, {
    //   registerApi: catalogImportPlugin.routes.importPage,
    // });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage 
        {...props} 
        auto 
        provider={{
            id: 'keycloak',
            title: 'Keycloak',
            message: 'Sign in',
            apiRef: keycloakAuthApiRef,
        }} 
      />
    ), 
  },
});

const HideScaffolderImportForNonAdmins: React.FC = () => {
  const identityApi = useApi(identityApiRef);
  const [hide, setHide] = React.useState<boolean>(true);
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const ownershipEntityRefs = identity.ownershipEntityRefs ?? [];
        const isAdmin = ownershipEntityRefs.includes('group:default/backstage-admins');
        if (mounted) setHide(!isAdmin);
      } catch (_e) {
        if (mounted) setHide(true);
      }
    })();
    return () => { mounted = false; };
  }, [identityApi]);
  if (!hide) return null;
  return <style>{`a[href="/catalog-import"] { display: none !important; }`}</style>;
};

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route path="/dashboards" element={<DashboardsPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <NotebookStylesAddon />
        <TextSize />
        <LightBox />
        <ExpandableNavigation />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <>
          {/* Increase default height of Scaffolder logs panel */}
          <style>{`
            .scaffolder-logs-height-override .react-resizable { height: 500px !important; }
            .scaffolder-logs-height-override .react-resizable > * { height: 100%; }
            /* Increase LogViewer font size slightly (~13px) */
            .scaffolder-logs-height-override [class*="BackstageLogViewer-log"] { font-size: 0.8125rem !important; }
            .scaffolder-logs-height-override [class*="BackstageLogViewer-line"] { font-size: 0.8125rem !important; }
            .scaffolder-logs-height-override .react-resizable [style*="will-change: transform"] {
              overflow-x: scroll !important;
            }
          `}</style>
          <div className="scaffolder-logs-height-override">
            <HideScaffolderImportForNonAdmins />
            <PreventDoubleCreate />
            <ScaffolderPage
              components={{
                TaskPageComponent: (CustomTaskPage as unknown as React.ComponentType<React.PropsWithChildren<{}>>),
                EXPERIMENTAL_TemplateListPageComponent: (TemplateListPageWithTasks as React.ComponentType<TemplateListPageProps>),
              }}
            />
          </div>
        </>
      }
    >
      <ScaffolderFieldExtensions>
        <HarborImageListExtension />
      </ScaffolderFieldExtensions>
    </Route>
    {/* <Route path="/api-docs" element={<ApiExplorerPage />} /> */}
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    {/* <Route path="/s3-viewer" element={<S3ViewerPage />} /> */}
    <Route path="/k8s-exec" element={<K8SExecPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
    <NotebookStylesAddon />
  </>,
);
